export interface EhrLabDocumentRef {
  type: "ehr_lab";
  clinic_id: number;
  patient_id: number;
  lab_id: number;
}

interface LabDownloadResult {
  success: boolean;
  data?: { download_url?: string };
  error?: { message?: string };
}

type DownloadEhrLab = (ref: EhrLabDocumentRef) => Promise<LabDownloadResult>;

const positiveInteger = (value: unknown): number | null => {
  const parsed =
    typeof value === "number"
      ? value
      : typeof value === "string"
      ? Number(value)
      : NaN;
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
};

/** Validate an opaque backend reference before using it in an authenticated API call. */
export const parseEhrLabDocumentRef = (
  value: unknown
): EhrLabDocumentRef | null => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  const candidate = value as Record<string, unknown>;
  if (candidate.type !== "ehr_lab") return null;

  const clinicId = positiveInteger(candidate.clinic_id);
  const patientId = positiveInteger(candidate.patient_id);
  const labId = positiveInteger(candidate.lab_id);
  if (clinicId == null || patientId == null || labId == null) return null;

  return {
    type: "ehr_lab",
    clinic_id: clinicId,
    patient_id: patientId,
    lab_id: labId,
  };
};

/** Exchange an opaque reference for a fresh authenticated HTTPS URL. */
export const resolveEhrLabDownloadUrl = async (
  ref: EhrLabDocumentRef,
  downloadEhrLab: DownloadEhrLab
): Promise<string> => {
  const result = await downloadEhrLab(ref);
  const downloadUrl = result.success ? result.data?.download_url : undefined;
  if (!downloadUrl) {
    throw new Error(result.error?.message || "No download URL returned");
  }
  const parsedUrl = new URL(downloadUrl);
  if (parsedUrl.protocol !== "https:") {
    throw new Error("Unexpected download URL protocol");
  }
  return parsedUrl.toString();
};
